import React from "react";
import { createPortal } from "react-dom";

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmStyle?: "danger" | "primary";
  onConfirm: () => void;
  onCancel?: () => void;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
  acknowledgeOnly?: boolean; // If true, only show Ok button
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmStyle = "primary",
  onConfirm,
  onCancel,
  confirmDisabled = false,
  children,
  acknowledgeOnly = false,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
          <p className="text-gray-600 leading-relaxed mb-6">{message}</p>
          {children}
          <div className="flex gap-3 mt-4">
            {acknowledgeOnly ? (
              <button
                onClick={onConfirm}
                className={`w-full px-6 py-3 font-semibold rounded-xl transition-colors ${
                  confirmStyle === "danger"
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-primary text-white hover:bg-primary/90"
                }`}
                disabled={confirmDisabled}
                style={
                  confirmDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}
                }
              >
                {confirmText || "Ok"}
              </button>
            ) : (
              <>
                <button
                  onClick={onCancel}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  className={`flex-1 px-6 py-3 font-semibold rounded-xl transition-colors ${
                    confirmStyle === "danger"
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-primary text-white hover:bg-primary/90"
                  }`}
                  disabled={confirmDisabled}
                  style={
                    confirmDisabled
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : {}
                  }
                >
                  {confirmText}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmModal;
